import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { cn } from '@/components/ui/utils'
import { useEditorStore } from '@/stores/editorStore'

import { describeSaveError, ensureSaved } from './useAutosave'

/**
 * The bar shown when the annotator was opened *from* somewhere (`?return=`), §9.6: a story
 * (`/stories/<uid>`) or the background's own page (`/backgrounds/<uid>`, where "Edit places" opens
 * this editor).
 *
 * Changes are saved only when the person asks, so with unsaved changes the button reads "Save and
 * go back": it saves, then navigates. When nothing is unsaved it simply goes back. A failed save
 * stays here with the reason (a newer version elsewhere opens the conflict dialog).
 */

export interface StoryReturnBarProps {
  /** `/stories/<uid>` or `/backgrounds/<uid>` — validated by the route's search schema. */
  returnTo: string
  /** The scene the user came from. Kept for the deep link; nothing re-bakes one scene today. */
  sceneId?: string
  onNavigate: (to: string) => void
  className?: string
}

export interface ReturnTarget {
  kind: 'story' | 'background' | 'page'
  /** What the bar says. */
  text: string
  /** The button when nothing is unsaved. */
  label: string
}

/** What a `return=` path points at, in words; null for something that is not a studio path. */
export function returnTarget(returnTo: string): ReturnTarget | null {
  if (!/^\/(?!\/)/.test(returnTo)) return null
  if (/^\/stories\/.+/.test(returnTo)) {
    return { kind: 'story', text: 'You came here from a story. When this picture looks right, save it and go back.', label: 'Back to the story' }
  }
  if (/^\/backgrounds(\/|$|\?)/.test(returnTo)) {
    return { kind: 'background', text: 'You came here from Backgrounds. When the places look right, save and go back.', label: 'Back to Backgrounds' }
  }
  return { kind: 'page', text: 'When this picture looks right, save it and go back.', label: 'Go back' }
}

export function StoryReturnBar({ returnTo, onNavigate, className }: StoryReturnBarProps) {
  const qc = useQueryClient()
  const target = returnTarget(returnTo)
  const dirty = useEditorStore((s) => s.dirty)

  const go = useMutation({
    mutationFn: async () => {
      const saved = await ensureSaved(qc)
      if (saved) return
      const err = useEditorStore.getState().saveError
      throw new Error(
        err?.kind === 'conflict'
          ? 'Someone saved a newer version — choose which one to keep first.'
          : (describeSaveError(err) ?? 'The picture could not be saved.'),
      )
    },
    onSuccess: () => {
      // Lists and summaries that show this stage (Backgrounds, the story page) read it again.
      void qc.invalidateQueries({ queryKey: ['stages'] })
      onNavigate(returnTo)
    },
    onError: (err: Error) => toast.error('Could not go back yet', { description: err.message }),
  })

  if (!target) return null

  return (
    <div className={cn('flex shrink-0 items-center gap-3 border-b border-line bg-accent-soft px-4 py-2 text-sm', className)} data-testid="return-bar" data-return={target.kind}>
      <ArrowLeft className="size-4 shrink-0" aria-hidden />
      <p className="flex-1">{target.text}</p>
      <Button variant="primary" size="sm" disabled={go.isPending} onClick={() => go.mutate()}>
        {go.isPending ? 'Saving…' : dirty ? 'Save and go back' : target.label}
      </Button>
    </div>
  )
}
